import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import type { Clip, ClipRequest, Player } from "@mdj/shared";
import { api, MOCK } from "./lib/api";
import { authEnabled, currentUser, login, logout } from "./lib/auth";
import { Backdrop } from "./components/Backdrop";
import { StepHeader, type StepId } from "./components/StepHeader";
import { Announcer } from "./components/Announcer";
import { Button, GlassCard } from "./components/Glass";
import { Lineup } from "./steps/Lineup";
import { SongStep } from "./steps/SongStep";
import { CutStep } from "./steps/CutStep";
import { PlayerStep } from "./steps/PlayerStep";
import { GameDay } from "./steps/GameDay";

type View = { kind: "lineup" } | { kind: "gameday" } | { kind: "song"; player: Player } | { kind: "announce"; player: Player };

// The lineup is home. Songs and announcements are set per player from it;
// Game day plays the lineup in order.
export function App() {
  const [view, setView] = useState<View>({ kind: "lineup" });
  const [step, setStep] = useState<StepId>(1);
  const [clip, setClip] = useState<Clip | null>(null);
  const [lastUrl, setLastUrl] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const [who, setWho] = useState<string | null>(null);
  // Nothing that talks to the API mounts until auth is resolved: the token
  // exchange on /callback is async and an early roster fetch would 401.
  const [auth, setAuth] = useState<"loading" | "in" | "out">(authEnabled() ? "loading" : "in");

  useEffect(() => {
    currentUser()
      .then((u) => { setWho(u?.profile.name ?? u?.profile.email ?? null); setAuth(!authEnabled() || u ? "in" : "out"); })
      .catch(() => setAuth("out"));
  }, []);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 2600); return () => clearTimeout(t); }, [toast]);

  const home = () => { setView({ kind: "lineup" }); setStep(1); setClip(null); };
  const cut = async (req: ClipRequest) => {
    setLastUrl(req.url);
    try { setClip(await api.createClip(req)); setStep(2); }
    catch (e) { setToast((e as Error).message); }
  };
  const assigned = (p: Player) => { setToast(`${p.name}'s walk-up is set 🎉`); home(); };
  const mode = view.kind === "gameday" ? "gameday" : "lineup";

  return (
    <>
      <Backdrop />
      <main className="app">
        <header className="brand">
          <h1><span>M</span>DJ</h1>
          {authEnabled() ? (
            who ? <Button ghost onClick={logout} style={{ minHeight: 40 }}>{who}</Button> : <Button ghost onClick={login} style={{ minHeight: 40 }}>Sign in</Button>
          ) : (
            <span className="who">{MOCK() ? "mock" : ""}</span>
          )}
        </header>

        {auth === "out" && (
          <GlassCard key="signin">
            <h2>Walk-up music, done in a minute.</h2>
            <p className="hint">Sign in with your eagledrive account to see the lineup.</p>
            <Button primary block onClick={login}>Sign in</Button>
          </GlassCard>
        )}
        {auth === "loading" && <p className="hint" style={{ textAlign: "center" }}>Signing you in…</p>}
        {auth === "in" && <>
        <nav className="glass modes" aria-label="Mode">
          {(["lineup", "gameday"] as const).map((v) => (
            <motion.button key={v} type="button" className={`mode${mode === v ? " on" : ""}`} onClick={() => (v === "lineup" ? home() : setView({ kind: "gameday" }))} whileTap={{ scale: 0.96 }} aria-pressed={mode === v}>
              {mode === v && <motion.span className="mode-pill" layoutId="mode-pill" transition={{ type: "spring", stiffness: 400, damping: 32 }} />}
              <span>{v === "lineup" ? "Lineup" : "Game day"}</span>
            </motion.button>
          ))}
        </nav>

        {view.kind === "song" && (
          <div className="row" style={{ justifyContent: "space-between" }}>
            <Button ghost onClick={home} style={{ flex: "0 0 auto" }}>← Lineup</Button>
            <span className="hint">Song for <b>{view.player.name}</b></span>
          </div>
        )}
        {view.kind === "song" && <StepHeader current={step} onJump={(s) => { if (s < step) setStep(s); }} />}

        <AnimatePresence mode="wait">
          {view.kind === "lineup" && <Lineup key="lineup" onSetSong={(p) => { setView({ kind: "song", player: p }); setStep(1); }} onAnnounce={(p) => setView({ kind: "announce", player: p })} />}
          {view.kind === "gameday" && <GameDay key="gd" />}
          {view.kind === "announce" && (
            <GlassCard key="announce">
              <h2>{view.player.number ? `#${view.player.number} ` : ""}{view.player.name}</h2>
              <Announcer player={view.player} onSaved={(p) => setView({ kind: "announce", player: p })} />
              <Button ghost onClick={home}>← Back to lineup</Button>
            </GlassCard>
          )}
          {view.kind === "song" && step === 1 && <SongStep key="s1" onCut={cut} initialUrl={lastUrl} />}
          {view.kind === "song" && step === 2 && clip && <CutStep key="s2" clip={clip} onDone={(c) => { setClip(c); setStep(3); }} onRedo={() => setStep(1)} />}
          {view.kind === "song" && step === 3 && clip && <PlayerStep key="s3" clip={clip} initialPlayerId={view.player.id} onAssigned={assigned} onBack={() => setStep(2)} />}
        </AnimatePresence>
        </>}
      </main>
      <AnimatePresence>
        {toast && (
          <motion.div className="glass glass-strong toast" role="status" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 24 }}>
            {toast}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
